import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CurrentUser } from '../api/client.js';
import { ChevronDownIcon, SignOutIcon, UserIcon } from './icons.js';

/**
 * The signed-in name, opening a small menu of account-level actions.
 *
 * It is a click-to-toggle menu rather than a hover menu: hover menus are
 * unreliable on touch screens and irritating with a trackpad, and nothing in
 * here is used often enough to justify saving a click.
 */
export function UserMenu({ user, onSignOut }: { user: CurrentUser; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const itemClass =
    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-body hover:bg-sunken hover:text-strong';

  return (
    <div ref={container} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-sunken"
      >
        <span className="text-xs leading-tight">
          <span className="block font-medium text-body">{user.displayName}</span>
          <span className="block text-faint">{user.roleLabel}</span>
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 text-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-44 overflow-hidden rounded-md border border-line bg-surface py-1 shadow-lg"
        >
          <Link role="menuitem" to="/account" className={itemClass} onClick={() => setOpen(false)}>
            <UserIcon className="h-4 w-4 text-muted" />
            Account
          </Link>
          <button
            role="menuitem"
            type="button"
            className={itemClass}
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            <SignOutIcon className="h-4 w-4 text-muted" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
